import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const BillyStartScreen = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || '';

  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/billy/start-status`, {
        credentials: 'include'
      });

      if (response.status === 401) {
        // Try to refresh the token before logging out
        const refreshRes = await fetch(`${API_URL}/api/auth/refresh`, {
          method: 'POST',
          credentials: 'include'
        });

        if (refreshRes.ok) {
          const retryResponse = await fetch(`${API_URL}/api/billy/start-status`, {
            credentials: 'include'
          });
          if (retryResponse.ok) {
            const data = await retryResponse.json();
            setStatus(data);
            return;
          }
        }

        // Refresh failed - actually logged out
        logout();
        return;
      }

      const data = await response.json();
      setStatus(data);
    } catch (err) {
      console.error('Error fetching status:', err);
    }
  }, [API_URL, logout]);

  // Fetch status every 2 seconds
  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 2000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  const handleStart = async () => {
    setLoading(true);
    setError(null);

    const maxRetries = 2;
    const retryDelayMs = 2000;

    for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
      try {
        console.log(`[Frontend] Starting stream - attempt ${attempt}/${maxRetries + 1}...`);

        let response = await fetch(`${API_URL}/api/billy/start`, {
          method: 'POST',
          credentials: 'include'
        });

        if (response.status === 401) {
          const refreshRes = await fetch(`${API_URL}/api/auth/refresh`, {
            method: 'POST',
            credentials: 'include'
          });

          if (refreshRes.ok) {
            response = await fetch(`${API_URL}/api/billy/start`, {
              method: 'POST',
              credentials: 'include'
            });
          } else {
            logout();
            return;
          }
        }

        const data = await response.json();

        if (data.success) {
          console.log(`[Frontend] Stream started successfully on attempt ${attempt}`);
          // Redirect to monitor screen
          setTimeout(() => {
            navigate('/billy/monitor');
          }, 1000);
          return; // Exit on success
        } else {
          // Backend returned an error
          const failureSource = data.failureSource || 'backend';
          console.error(`[Frontend] Attempt ${attempt} failed - Source: ${failureSource.toUpperCase()} - Error: ${data.message || data.error}`);

          if (attempt <= maxRetries) {
            console.log(`[Frontend] Retrying in ${retryDelayMs}ms...`);
            await new Promise(resolve => setTimeout(resolve, retryDelayMs));
          } else {
            // All retries exhausted
            console.error(`[Frontend] All ${maxRetries + 1} attempts failed. Source: ${failureSource.toUpperCase()}`);
            setError('whatsapp');
            setLoading(false);
            return;
          }
        }
      } catch (err) {
        // Network or frontend error
        console.error(`[Frontend] Attempt ${attempt} failed - Source: FRONTEND (network/connection) - Error: ${err.message}`);

        if (attempt <= maxRetries) {
          console.log(`[Frontend] Retrying in ${retryDelayMs}ms...`);
          await new Promise(resolve => setTimeout(resolve, retryDelayMs));
        } else {
          // All retries exhausted
          console.error(`[Frontend] All ${maxRetries + 1} attempts failed. Source: FRONTEND (network/connection)`);
          setError('whatsapp');
          setLoading(false);
          return;
        }
      }
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (!status) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading...</div>
      </div>
    );
  }

  const isLive = status.isLive;

  return (
    <div style={styles.container}>
      {/* User Info Bar */}
      <div style={styles.userBar} className="user-bar">
        <span style={styles.userEmail}>{user?.email}</span>
        <button onClick={handleLogout} style={styles.logoutButton}>
          Sign out
        </button>
      </div>

      <div style={styles.content}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>Days in the Life of Mz. Porter</h1>
          <p style={styles.subtitle}>Start Your Show</p>
        </div>

        {/* Status Indicator */}
        <div style={styles.statusSection}>
          <div style={{
            ...styles.statusDot,
            backgroundColor: isLive ? '#4CAF50' : '#f44336',
            animation: isLive ? 'none' : 'pulse 2s infinite'
          }} />
          <span style={styles.statusText}>
            {status.streamStatus}
          </span>
        </div>

        {/* Optional Info */}
        {status.cameraOn !== null && (
          <div style={styles.infoBox}>
            <p>Camera: {status.cameraOn ? 'On' : 'Off'}</p>
          </div>
        )}

        {status.streamingSince && (
          <div style={styles.infoBox}>
            <p>Streaming for: {status.streamingSince}</p>
          </div>
        )}

        {/* Start Button */}
        {!isLive && (
          <>
            <button
              onClick={handleStart}
              disabled={loading}
              style={{
                ...styles.startButton,
                opacity: loading ? 0.6 : 1,
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'STARTING...' : 'START'}
            </button>
            <button
              onClick={() => navigate('/billy/monitor')}
              style={styles.viewMonitorButton}
            >
              View Monitor
            </button>
          </>
        )}

        {/* Already Live Message */}
        {isLive && (
          <div style={styles.liveMessage}>
            <p style={styles.liveText}>You're already LIVE!</p>
            <button
              onClick={() => navigate('/billy/monitor')}
              style={styles.monitorButton}
            >
              Go to Monitor
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={styles.error}>
            {error === 'whatsapp' ? (
              <>
                There seems to be an issue with the start. Please send a WhatsApp message to{' '}
                <a
                  href="https://wa.me/447440348430"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={styles.whatsappLink}
                >
                  +44 7440 348430
                </a>
                {' '}with a screenshot.
              </>
            ) : (
              error
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @media (max-width: 768px) {
          .user-bar {
            left: 20px !important;
            right: 20px !important;
            justify-content: space-between !important;
          }
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1a1a1a',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    position: 'relative'
  },
  userBar: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  },
  userEmail: {
    color: '#888',
    fontSize: '14px'
  },
  logoutButton: {
    backgroundColor: 'transparent',
    color: '#888',
    border: '1px solid #3a3a3a',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  content: {
    textAlign: 'center',
    maxWidth: '500px',
    padding: '40px'
  },
  header: {
    marginBottom: '40px'
  },
  title: {
    fontSize: '48px',
    fontWeight: 'bold',
    margin: '0 0 10px 0'
  },
  subtitle: {
    fontSize: '18px',
    color: '#888',
    margin: '0'
  },
  statusSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '30px',
    gap: '12px'
  },
  statusDot: {
    width: '16px',
    height: '16px',
    borderRadius: '50%'
  },
  statusText: {
    fontSize: '18px',
    textTransform: 'uppercase',
    fontWeight: '500'
  },
  infoBox: {
    backgroundColor: '#2a2a2a',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '15px',
    fontSize: '14px'
  },
  startButton: {
    backgroundColor: '#f44336',
    color: '#ffffff',
    border: 'none',
    borderRadius: '12px',
    padding: '24px 60px',
    fontSize: '32px',
    fontWeight: 'bold',
    marginTop: '30px',
    transition: 'transform 0.2s, box-shadow 0.2s',
    boxShadow: '0 4px 20px rgba(244, 67, 54, 0.4)'
  },
  viewMonitorButton: {
    backgroundColor: '#555',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '14px 32px',
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    margin: '20px auto 0',
    display: 'block'
  },
  liveMessage: {
    marginTop: '30px'
  },
  liveText: {
    fontSize: '24px',
    color: '#4CAF50',
    marginBottom: '20px'
  },
  monitorButton: {
    backgroundColor: '#2196F3',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '14px 32px',
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  error: {
    backgroundColor: '#f44336',
    padding: '12px',
    borderRadius: '8px',
    marginTop: '20px',
    fontSize: '14px'
  },
  whatsappLink: {
    color: '#ffffff',
    fontWeight: 'bold',
    textDecoration: 'underline'
  },
  loading: {
    fontSize: '24px',
    color: '#888'
  }
};

export default BillyStartScreen;
